/**
 * Undo/redo.
 *
 * Snapshot based rather than operation based. The editor deliberately delegates
 * a lot of editing to the browser (IME, autocorrect, soft keyboards, drag and
 * drop); inverting those would mean diffing the DOM anyway, which is more code
 * than storing strings for fields this size.
 *
 * The browser's own undo stack is never consulted: the component cancels the
 * `historyUndo`/`historyRedo` input events and serves them from here, so the
 * native stack can never be invoked and its staleness cannot matter.
 */

import type { Bookmark } from './bookmark';

export type HistoryKind = 'type' | 'delete' | 'command';

/** Consecutive edits of the same kind inside this window collapse into one step. */
const COALESCE_WINDOW_MS = 700;

/** Beyond this size, stop coalescing and keep fewer entries. */
const LARGE_DOCUMENT_CHARS = 200_000;

export interface HistoryEntry {
  html: string;
  bookmark: Bookmark | null;
  kind: HistoryKind;
  at: number;
}

export class EditorHistory {
  private entries: HistoryEntry[] = [];
  private index = -1;
  private breakRun = false;

  constructor(private readonly limit = 100) {}

  get canUndo(): boolean {
    return this.index > 0;
  }

  get canRedo(): boolean {
    return this.index >= 0 && this.index < this.entries.length - 1;
  }

  /** Establishes the baseline, so the first undo returns to the loaded value. */
  seed(html: string, mark: Bookmark | null): void {
    this.entries = [{ html, bookmark: mark, kind: 'command', at: Date.now() }];
    this.index = 0;
    this.breakRun = false;
  }

  /**
   * Ends the current run, so the next edit starts a fresh undo step. Called at
   * word boundaries -- undoing a whole word at a time is what makes undo feel
   * right, rather than character by character.
   */
  checkpoint(): void {
    this.breakRun = true;
  }

  push(html: string, mark: Bookmark | null, kind: HistoryKind): void {
    const current = this.entries[this.index];

    if (current && current.html === html) {
      current.bookmark = mark;
      return;
    }

    const canCoalesce = !!current
      && !this.breakRun
      && kind === current.kind
      && kind !== 'command'
      && Date.now() - current.at < COALESCE_WINDOW_MS
      && html.length < LARGE_DOCUMENT_CHARS;

    this.breakRun = false;

    if (canCoalesce && current) {
      current.html = html;
      current.bookmark = mark;
      current.at = Date.now();
      return;
    }

    this.entries.length = this.index + 1;
    this.entries.push({ html, bookmark: mark, kind, at: Date.now() });

    const cap = html.length >= LARGE_DOCUMENT_CHARS ? 30 : this.limit;
    while (this.entries.length > cap) {
      this.entries.shift();
    }
    this.index = this.entries.length - 1;
  }

  undo(): HistoryEntry | null {
    if (!this.canUndo) {
      return null;
    }
    this.index -= 1;
    this.breakRun = true;
    return this.entries[this.index] ?? null;
  }

  redo(): HistoryEntry | null {
    if (!this.canRedo) {
      return null;
    }
    this.index += 1;
    this.breakRun = true;
    return this.entries[this.index] ?? null;
  }

  clear(): void {
    this.entries = [];
    this.index = -1;
    this.breakRun = false;
  }
}
